import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Save, X } from "lucide-react";

const humanizeKey = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const isDateOnly = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const formatValue = (key, value) => {
  if (value === null || typeof value === "undefined" || value === "") return "-";

  if (key === "step") {
    const step = Number(value);
    if (step === 3) return "Paid (Step 3)";
    if (step === 2) return "Incomplete (Step 2)";
    if (Number.isFinite(step)) return `Step ${step}`;
    return String(value);
  }

  if (key === "charge" || key === "Charge") {
    const num = Number(value);
    return Number.isFinite(num) ? `$${num.toFixed(2)}` : String(value);
  }

  if (key === "cleaningHoursApprox") {
    const num = Number(value);
    return Number.isFinite(num) ? `${num} hrs` : String(value);
  }

  if (key.endsWith("Time") && typeof value === "string" && value.length >= 5 && /^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }

  if (
    key.toLowerCase().includes("date") ||
    key.toLowerCase().includes("created") ||
    key.toLowerCase().includes("scheduled")
  ) {
    if (value instanceof Date) return value.toLocaleString();
    if (typeof value === "string" && isDateOnly(value)) return value;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "-";

  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : "-";
  if (typeof value === "object") return JSON.stringify(value, null, 2);

  return String(value);
};

const buildEntries = (booking) => {
  if (!booking) return [];

  const keys = Object.keys(booking);
  const hide = new Set();
  if (keys.includes("charge")) hide.add("Charge");
  if (keys.includes("extraHours")) hide.add("ExtraHours");

  const preferredOrder = [
    "serviceId",
    "step",
    "creationDate",
    "firstName",
    "lastName",
    "email",
    "phone",
    "serviceType",
    "cleanType",
    "frequency",
    "bedrooms",
    "bathrooms",
    "hours",
    "minutes",
    "preferredDate",
    "preferredTime",
    "streetAddress",
    "suburb",
    "postcode",
    "getInOption",
    "parkOption",
    "specialInstructions",
    "carpetOptions",
    "extraHours",
    "charge",
    "cleaningHoursApprox",
    "assignmentId",
    "employeeId",
    "scheduledAt",
    "jobStatus",
    "employeeName",
    "employeeEmail",
    "employeeContact",
  ];

  const used = new Set();
  const result = [];

  for (const key of preferredOrder) {
    if (!Object.prototype.hasOwnProperty.call(booking, key)) continue;
    if (hide.has(key)) continue;
    used.add(key);
    result.push({ key, label: humanizeKey(key), value: formatValue(key, booking[key]) });
  }

  const remaining = keys
    .filter((key) => !used.has(key) && !hide.has(key))
    .sort((a, b) => a.localeCompare(b));

  for (const key of remaining) {
    result.push({ key, label: humanizeKey(key), value: formatValue(key, booking[key]) });
  }

  return result;
};

const editableFields = [
  { name: "firstName", label: "First name", type: "text" },
  { name: "lastName", label: "Last name", type: "text" },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "serviceType", label: "Service type", type: "text" },
  { name: "cleanType", label: "Clean type", type: "text" },
  { name: "frequency", label: "Frequency", type: "text" },
  { name: "bedrooms", label: "Bedrooms", type: "number" },
  { name: "bathrooms", label: "Bathrooms", type: "number" },
  { name: "hours", label: "Hours", type: "number" },
  { name: "minutes", label: "Minutes", type: "number" },
  { name: "preferredDate", label: "Preferred date", type: "date" },
  { name: "preferredTime", label: "Preferred time", type: "time" },
  { name: "streetAddress", label: "Street address", type: "text", wide: true },
  { name: "suburb", label: "Suburb", type: "text" },
  { name: "postcode", label: "Postcode", type: "text" },
  { name: "carpetOptions", label: "Carpet options", type: "text" },
  { name: "extraHours", label: "Extra hours", type: "number" },
  { name: "charge", label: "Charge", type: "number", step: "0.01" },
  { name: "getInOption", label: "How can we get in", type: "textarea", wide: true },
  { name: "parkOption", label: "Where can we park", type: "textarea", wide: true },
  { name: "specialInstructions", label: "Special instructions", type: "textarea", wide: true },
];

const formValueFor = (booking, field) => {
  const raw = field.name === "charge" ? booking?.charge ?? booking?.Charge : booking?.[field.name];
  if (raw === null || typeof raw === "undefined") return "";
  if (field.type === "time" && typeof raw === "string") return raw.slice(0, 5);
  return String(raw);
};

const buildEditForm = (booking) =>
  editableFields.reduce((acc, field) => {
    acc[field.name] = formValueFor(booking, field);
    return acc;
  }, {});

export default function BookingDetailsModal({ open, booking, loading, error, onClose, onSave }) {
  const closeRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [form, setForm] = useState({});

  const entries = useMemo(() => buildEntries(booking), [booking]);
  const canEdit = Boolean(onSave) && Number(booking?.step) === 3 && !loading && !error;

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (closeRef.current) closeRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setSaveError("");
      setForm({});
      return;
    }

    setEditing(false);
    setSaveError("");
    setForm(buildEditForm(booking));
  }, [open, booking]);

  if (!open) return null;

  const title = booking?.serviceId ? `Booking #${booking.serviceId}` : "Booking details";

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError("");
    setForm(buildEditForm(booking));
  };

  const saveChanges = async () => {
    if (!onSave || !booking?.serviceId) return;

    setSaving(true);
    setSaveError("");
    try {
      const updated = await onSave(form);
      setForm(buildEditForm(updated || booking));
      setEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || "Failed to save booking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rs-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="rs-card rs-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="rs-modal__header">
          <div>
            <div className="rs-modal__title">{editing ? `${title} edit` : title}</div>
            {loading ? <div className="rs-muted">Loading...</div> : null}
            {!loading && error ? <div className="rs-muted">{error}</div> : null}
            {!loading && booking && Number(booking.step) !== 3 ? (
              <div className="rs-muted">Only paid bookings can be edited.</div>
            ) : null}
          </div>
          <div className="rs-modal__actions">
            {canEdit && !editing ? (
              <button
                type="button"
                className="rs-btn rs-btn--outline"
                onClick={() => setEditing(true)}
                disabled={saving}
              >
                <Pencil size={16} />
                Edit
              </button>
            ) : null}
            {editing ? (
              <>
                <button type="button" className="rs-btn rs-btn--ghost" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="rs-btn rs-btn--primary" onClick={saveChanges} disabled={saving}>
                  <Save size={16} />
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="rs-icon-btn"
              onClick={() => onClose?.()}
              aria-label="Close"
              title="Close"
              ref={closeRef}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="rs-modal__body">
          {loading ? (
            <div className="rs-muted">Loading booking details...</div>
          ) : booking ? (
            editing ? (
              <div className="rs-edit-form">
                <div className="rs-edit-grid">
                  {editableFields.map((field) => (
                    <label
                      className={`rs-field rs-edit-field${field.wide ? " rs-edit-field--wide" : ""}`}
                      key={field.name}
                    >
                      <span>{field.label}</span>
                      {field.type === "textarea" ? (
                        <textarea
                          className="rs-input rs-textarea"
                          name={field.name}
                          value={form[field.name] ?? ""}
                          onChange={updateField}
                          rows={3}
                        />
                      ) : (
                        <input
                          className="rs-input"
                          name={field.name}
                          type={field.type}
                          value={form[field.name] ?? ""}
                          onChange={updateField}
                          min={field.type === "number" ? "0" : undefined}
                          step={field.step || (field.type === "number" ? "1" : undefined)}
                        />
                      )}
                    </label>
                  ))}
                </div>
                {saveError ? <div className="rs-form-error">{saveError}</div> : null}
              </div>
            ) : (
              <div className="rs-kv">
                {entries.map((entry) => (
                  <div className="rs-kv__row" key={entry.key}>
                    <div className="rs-kv__k">{entry.label}</div>
                    <div className="rs-kv__v rs-kv__v--modal">{entry.value}</div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="rs-muted">No booking data.</div>
          )}
        </div>
      </div>
    </div>
  );
}
